/**
 * The shape of Spectrum's help content. One source feeds everything the Guide
 * shows: the side panel on each screen, the guided tours, the Help centre at
 * /help, and the Markdown manuals in docs/manuals — so a sentence fixed here
 * is fixed everywhere at once.
 *
 * Content is written for the person at the desk (the office staff, the
 * teacher, the accountant), not for a developer: screen and button names
 * exactly as the screen spells them, no API paths, no table names, and
 * permission names only in `access`, where an administrator reads them to
 * grant a role.
 *
 * Each app owns its manual beside its screens (`src/app/help/`), and hands it
 * to the Guide with `provideSpectrumGuide`. When a screen changes, its topic
 * changes in the same PR.
 */

/** One job a person comes to a screen to do, as numbered steps. */
export interface HelpTask {
    title: string;
    /** When you would do this — one sentence. */
    when?: string;
    steps: string[];
    /** What you should see when it has worked. */
    result?: string;
}

export interface HelpTerm {
    term: string;
    meaning: string;
}

export interface HelpFaq {
    q: string;
    a: string;
}

/** A message a screen shows, what it means and what to do about it. */
export interface HelpError {
    /** The server's error code, when the message carries one. */
    code?: string;
    /** The words, or the gist, of what the screen shows. */
    message: string;
    meaning: string;
    fix: string;
}

/**
 * One step of a guided tour. `anchor` names a `data-guide` attribute on the
 * screen; the tour highlights that element and puts the card beside it. A
 * step whose anchor is missing (a permission hid the button, the list is
 * empty) is shown as a centred card, so a tour never stalls on a screen that
 * looks different for this user.
 */
export interface HelpTourStep {
    anchor?: string;
    title: string;
    body: string;
}

export interface HelpTopic {
    /** Stable id, `<module>.<screen>` — used in links between topics and in the Help centre's URL. */
    id: string;
    /**
     * The in-app route the screen is served at, with `:param` segments
     * (`/students/:id`). The topic for a URL is the one whose pattern matches
     * it most specifically.
     */
    route: string;
    /** Other routes this topic answers for (a print view, a "new" form, a redirect). */
    aliases?: string[];
    title: string;
    /** Chapter id within the module's manual. */
    chapter: string;
    /** What the screen is for, in one to three sentences. */
    summary: string;
    /** Who opens it and how often — "the fee cashier, every working day". */
    audience?: string;
    /** Who may use it, in words an administrator can act on. */
    access?: string;
    /** How to reach it when it is not in the menu. */
    reach?: string;
    tasks: HelpTask[];
    /** Columns, fields, chips and statuses explained. */
    fields?: HelpTerm[];
    tips?: string[];
    /** Rules the screen enforces that are easy to trip over. */
    cautions?: string[];
    errors?: HelpError[];
    faqs?: HelpFaq[];
    /** Topic ids worth reading next. */
    related?: string[];
    tour?: HelpTourStep[];
    /** Extra words people search with ("tc", "leaving certificate"). */
    keywords?: string[];
}

export interface HelpWorkflowStep {
    text: string;
    /** The topic that covers this step, if any. */
    topic?: string;
}

/** A job that crosses screens: starting a new academic year, running payroll. */
export interface HelpWorkflow {
    id: string;
    title: string;
    goal: string;
    /** How often it is done — "once a year, before school reopens". */
    cadence?: string;
    steps: HelpWorkflowStep[];
}

export interface HelpChapter {
    id: string;
    title: string;
    intro: string;
}

/** One module's manual. */
export interface HelpManual {
    /** The module's id, the same word its topic ids start with: "finance". */
    module: string;
    title: string;
    intro: string;
    chapters: HelpChapter[];
    workflows: HelpWorkflow[];
    glossary: HelpTerm[];
    /** Who does what in this module: the roles and what each may do. */
    roles?: HelpTerm[];
    topics: HelpTopic[];
}
